import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "../../theme/theme";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaletteIcon from "@mui/icons-material/Palette";
import axios from "axios";
import AuthContext from "../../context/auth/AuthContext";
import { TrackNowButton, TrackNowSelect, TrackNowTextField } from "../ui";
import { tokens } from "../../theme/tokens";
import { useCommonDetailsContext } from "../../context/Common/common";
import Loader from "../common/Loader";
import CustomDialog from "../common/CustomDialog";

const API_BASE_URL = process.env.REACT_APP_TRKNW_API_BASE_URL;

const STEP_TABS = [
    "1. Brand Assets",
    "2. Colors & Fonts",
    "3. Preview",
    "4. Save Settings",
    "5. Version History",
];

const FONT_OPTIONS = [
    // Standard & Modern Sans-Serif Fonts
    "Area Normal",
    "Roboto",
    "Inter",
    "Poppins",
    "Open Sans",
    "Lato",
    "Montserrat",
    "Work Sans",
    "Plus Jakarta Sans",
    "DM Sans",
    "Nunito",
    "Raleway",
    "Fira Sans",
    "Rubik",
    "Quicksand",
    "Noto Sans",
    "Josefin Sans",
    "Arial",
    "Verdana",
    "Trebuchet MS",

    // Classic & Standard Serif / Formal Fonts
    "Times New Roman",
    "Georgia",
    "Garamond",
    "Merriweather",
    "Playfair Display",
    "Baskerville",
    "Cambria",
    "Didot",

    // Display & Stylized Fonts
    "Oswald",
    "Newton",
    "Courier New",
];

export interface IBrandingForm {
    primaryColor: string;
    backgroundColor: string;
    fontFamily: string;
    logoUrl?: string;
    logoDataUrl?: string;
    logoFile?: File | null;
    logoPreviewUrl?: string;
}

export const DEFAULT_BRANDING_VALUES: IBrandingForm = {
    primaryColor: "#001a77",
    backgroundColor: "#001a77",
    fontFamily: "Roboto",
    logoUrl: "",
    logoDataUrl: "",
    logoFile: null,
    logoPreviewUrl: "",
};

const fileToDataUrl = (file: File): Promise<string | null> =>
    new Promise((resolve, reject) => {
        if (!file) {
            resolve(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleString();
    } catch {
        return dateStr;
    }
};

const LogoUploadField: React.FC<{
    fileInputKey: number;
    logoFile: File | null;
    logoPreviewUrl: string;
    formLogoUrl?: string;
    onFileChange: (file: File | null) => void;
    onClear: () => void;
    error?: string;
    helperText?: string;
}> = ({
    fileInputKey,
    logoFile,
    logoPreviewUrl,
    formLogoUrl,
    onFileChange,
    onClear,
    error,
    helperText,
}) => {
        const fileInputRef = useRef<HTMLInputElement>(null);

        const getDisplayName = () => {
            if (logoFile?.name) return logoFile.name;
            if (formLogoUrl) {
                const parts = formLogoUrl.split("/").pop()?.split("?")[0];
                return parts || formLogoUrl;
            }
            if (logoPreviewUrl) {
                if (logoPreviewUrl.startsWith("data:")) return "Selected Image";
                const parts = logoPreviewUrl.split("/").pop()?.split("?")[0];
                return parts || "Selected Image";
            }
            return "No file chosen";
        };

        const hasFile = Boolean(logoFile || logoPreviewUrl || formLogoUrl);

        return (
            <Stack spacing={0.5}>
                <Typography sx={{ color: tokens.colors.grey, fontSize: "14px" }}>
                    Logo Upload
                </Typography>
                <input
                    key={fileInputKey}
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        onFileChange(file);
                    }}
                />
                <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        height: "48px",
                        minHeight: "48px",
                        borderRadius: "10px",
                        border: `1px solid ${error ? tokens.colors.error : tokens.colors.grey}`,
                        backgroundColor: tokens.colors.offWhite,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: "10px",
                        cursor: "pointer",
                        boxSizing: "border-box",
                        "&:hover": {
                            borderColor: error ? tokens.colors.error : tokens.colors.offBlack,
                        },
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflow: "hidden", pr: 1 }}>
                        <Button
                            variant="outlined"
                            component="span"
                            size="small"
                            sx={{
                                textTransform: "none",
                                fontSize: "13px",
                                fontWeight: 500,
                                color: tokens.colors.offBlack,
                                backgroundColor: "#fff",
                                borderColor: tokens.colors.midGrey,
                                borderRadius: "6px",
                                py: "4px",
                                px: "10px",
                                flexShrink: 0,
                                "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                    borderColor: tokens.colors.grey,
                                },
                            }}
                        >
                            Choose File
                        </Button>
                        <Typography
                            noWrap
                            sx={{
                                fontSize: "14px",
                                color: hasFile ? tokens.colors.offBlack : tokens.colors.grey,
                                fontWeight: hasFile ? 500 : 400,
                            }}
                        >
                            {getDisplayName()}
                        </Typography>
                    </Stack>
                    {hasFile && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (fileInputRef.current) fileInputRef.current.value = "";
                                onClear();
                            }}
                            sx={{
                                color: tokens.colors.grey,
                                "&:hover": { color: tokens.colors.offBlack },
                            }}
                            title="Clear logo"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
                {helperText && (
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: error ? tokens.colors.error : tokens.colors.grey,
                            mt: "4px",
                            ml: 0,
                        }}
                    >
                        {helperText}
                    </Typography>
                )}
            </Stack>
        );
    };

const ColorField: React.FC<{
    label: string;
    value: string;
    onChange: (val: string) => void;
    error?: string;
}> = ({ label, value, onChange, error }) => (
    <Grid item xs={12} md={6}>
        <Stack spacing={1}>
            <Typography sx={{ color: tokens.colors.grey, fontSize: "14px" }}>{label}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <Box
                    component="input"
                    type="color"
                    value={value || "#000000"}
                    onChange={(e: any) => onChange(e.target.value)}
                    sx={{
                        width: 48,
                        height: 48,
                        border: `1px solid ${tokens.colors.midGrey}`,
                        borderRadius: "12px",
                        cursor: "pointer",
                        backgroundColor: "transparent",
                        p: "4px",
                        flexShrink: 0,
                    }}
                />
                <TrackNowTextField
                    value={value || ""}
                    onChange={(e: any) => onChange(e.target.value)}
                    error={Boolean(error)}
                    helperText={error}
                    fullWidth
                    placeholder="#001a77"
                />
            </Stack>
        </Stack>
    </Grid>
);

const loadDynamicFonts = () => {
    const SYSTEM_FONTS = [
        "Times New Roman", "Georgia", "Garamond", "Arial", "Verdana",
        "Trebuchet MS", "Courier New", "Baskerville", "Cambria", "Didot"
    ];
    FONT_OPTIONS.forEach((fontName) => {
        if (!SYSTEM_FONTS.includes(fontName)) {
            const id = `dynamic-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
            if (!document.getElementById(id)) {
                const link = document.createElement("link");
                link.id = id;
                link.rel = "stylesheet";
                link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;600;700&display=swap`;
                document.head.appendChild(link);
            }
        }
    });
};

const SearchableFontSelect: React.FC<{
    value: string;
    onChange: (e: any) => void;
    error?: boolean;
    helperText?: string;
}> = ({ value, onChange, error, helperText }) => {
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        loadDynamicFonts();
    }, []);

    const filteredFonts = useMemo(() => {
        if (!searchTerm.trim()) return FONT_OPTIONS;
        return FONT_OPTIONS.filter((font) =>
            font.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    return (
        <TrackNowSelect
            value={value}
            onChange={onChange}
            label="Font Family"
            error={Boolean(error)}
            helperText={helperText}
            fullWidth
            renderValue={(selected: any) => (
                <span className="tn-font-preview-item" style={{ fontFamily: `'${selected}', sans-serif !important`, '--tn-preview-font': `'${selected}', sans-serif` } as React.CSSProperties}>
                    {selected}
                </span>
            )}
            MenuProps={{
                autoFocus: false,
                PaperProps: {
                    style: { maxHeight: 380, width: 340 }
                }
            }}
        >
            <Box
                sx={{ p: 1, position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 2, borderBottom: `1px solid ${tokens.colors.midGrey}` }}
                onClick={(e: any) => e.stopPropagation()}
                onKeyDown={(e: any) => e.stopPropagation()}
            >
                <TrackNowTextField
                    placeholder="Search font..."
                    value={searchTerm}
                    onChange={(e: any) => setSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                />
            </Box>

            {filteredFonts.length > 0 ? (
                filteredFonts.map((fontName) => (
                    <MenuItem
                        key={fontName}
                        value={fontName}
                        className="tn-font-preview-item"
                        style={{ '--tn-preview-font': `'${fontName}', sans-serif` } as React.CSSProperties}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.2,
                            px: 2,
                        }}
                    >
                        <Typography
                            className="tn-font-preview-item"
                            style={{ '--tn-preview-font': `'${fontName}', sans-serif` } as React.CSSProperties}
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px"
                            }}
                        >
                            {fontName}
                        </Typography>
                        <Typography
                            className="tn-font-preview-item"
                            style={{ '--tn-preview-font': `'${fontName}', sans-serif` } as React.CSSProperties}
                            sx={{
                                fontSize: "12px",
                                color: tokens.colors.grey
                            }}
                        >
                            Aa Bb 123
                        </Typography>
                    </MenuItem>
                ))
            ) : (
                <MenuItem disabled sx={{ justifyContent: "center", py: 2 }}>
                    No fonts found
                </MenuItem>
            )}
        </TrackNowSelect>
    );
};

export interface IPendingBrandingAction {
    type: "CREATE" | "UPDATE_STATUS";
    brandingId?: number;
    row?: any;
}

export interface CustomerBrandingModalProps {
    open: boolean;
    onClose: () => void;
    customerId?: number | null;
    customerName?: string;
    brandingConfig: IBrandingForm;
    setBrandingConfig: React.Dispatch<React.SetStateAction<IBrandingForm>>;
    onBrandingSaved?: (config: IBrandingForm, action?: IPendingBrandingAction) => void;
    pendingBrandingAction?: IPendingBrandingAction | null;
}

export const CustomerBrandingModal: React.FC<CustomerBrandingModalProps> = ({
    open,
    onClose,
    customerId,
    customerName,
    brandingConfig,
    setBrandingConfig,
    onBrandingSaved,
    pendingBrandingAction,
}) => {
    const authContext = useContext(AuthContext);
    const { authUser } = authContext || {};
    const { publishedBranding, setIsPublishedBranding, validateParentData, fetchValidateParent, brandingCacheRef } = useCommonDetailsContext();

    const [activeStep, setActiveStep] = useState(0);
    const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);

    const [fileInputKey, setFileInputKey] = useState(0);
    const [logoFile, setLogoFile] = useState<File | null>(brandingConfig.logoFile || null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>(brandingConfig.logoPreviewUrl || brandingConfig.logoDataUrl || "");
    const [form, setForm] = useState<IBrandingForm>({
        primaryColor: brandingConfig.primaryColor || "#001a77",
        backgroundColor: brandingConfig.backgroundColor || tokens.colors.offWhite,
        fontFamily: brandingConfig.fontFamily || "Roboto",
        logoUrl: brandingConfig.logoUrl || "",
        logoDataUrl: brandingConfig.logoDataUrl || "",
    });

    const primaryColor = form.primaryColor || brandingConfig?.primaryColor || publishedBranding?.primaryColor || tokens.colors.connectedPurple;

    const modalTheme = useMemo(
        () =>
            createAppTheme(false, {
                primaryColor: primaryColor,
                backgroundColor: form.backgroundColor,
                fontFamily: form.fontFamily,
            }),
        [primaryColor, form.backgroundColor, form.fontFamily]
    );
    const [published, setPublished] = useState<any>(null);
    const [historyRows, setHistoryRows] = useState<any[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isPublishing, setIsPublishing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");
    const [openDialog, setOpenDialog] = useState(false);

    const isEditMode = Boolean(customerId && customerId > 0);

    const fetchBranding = async (custNumber: number) => {
        if (!custNumber) return null;
        try {
            const res = await axios.get(
                `${API_BASE_URL}/Application/GetBrandingConfiguration?CustomerId=${custNumber}&CustomerType=Parent`
            );
            const data = res.data?.data || (Array.isArray(res.data) ? res.data[0] : res.data);
            if (data && (data.primaryColor || data.PrimaryColor || data.customerId || data.CustomerId || data.brandingId || data.BrandingId)) {
                const rawBgColor = data.backgroundColor || data.BackgroundColor;
                const normalized = {
                    brandingId: data.brandingId ?? data.BrandingId ?? data.id ?? data.Id,
                    customerId: data.customerId ?? data.CustomerId ?? custNumber,
                    primaryColor: data.primaryColor || data.PrimaryColor || "#001a77",
                    // Treat stored "#ffffff" as unset — it was the old bad default (tokens.colors.offWhite = "#ffffff")
                    backgroundColor: (!rawBgColor || rawBgColor === "#ffffff") ? "#001a77" : rawBgColor,
                    fontFamily: data.fontFamily || data.FontFamily || "Roboto",
                    logoDataUrl: data.logoDataUrl || data.LogoDataUrl || "",
                    userId: data.userId ?? data.UserId ?? "",
                    userName: data.userName || data.UserName || data.createUser || data.CreateUser || "",
                    createUser: data.createUser || data.CreateUser || "",
                    createDate: data.createDate || data.CreateDate || data.publishedAt || data.PublishedAt || "",
                    updateUser: data.updateUser || data.UpdateUser || "",
                    updateDate: data.updateDate || data.UpdateDate || "",
                    version: data.version ?? data.Version,
                };
                setPublished(normalized);
                if (!pendingBrandingAction) {
                    setForm((prev) => ({
                        ...prev,
                        primaryColor: normalized.primaryColor,
                        backgroundColor: normalized.backgroundColor,
                        fontFamily: normalized.fontFamily,
                        logoDataUrl: normalized.logoDataUrl,
                    }));
                    if (normalized.logoDataUrl) {
                        setLogoPreviewUrl(normalized.logoDataUrl);
                    }
                }
                return normalized;
            } else {
                setPublished(null);
                return null;
            }
        } catch (error) {
            console.error("Error fetching branding configuration:", error);
            return null;
        }
    };

    const fetchHistory = async (custNumber: number) => {
        if (!custNumber) return;
        try {
            const res = await axios.get(
                `${API_BASE_URL}/Application/GetBrandingConfigurationHistory?CustomerId=${custNumber}`
            );
            let list: any[] = [];
            const data = res.data;
            if (Array.isArray(data)) {
                list = data;
            } else if (data && typeof data === "object") {
                if (Array.isArray(data.result || data.Result)) {
                    list = data.result || data.Result;
                } else if (Array.isArray(data.history || data.History)) {
                    list = data.history || data.History;
                } else if (Array.isArray(data.data || data.Data)) {
                    list = data.data || data.Data;
                } else {
                    list = [data];
                }
            }
            setHistoryRows(list);
        } catch (error) {
            console.error("Error fetching branding history:", error);
            setHistoryRows([]);
        }
    };

    useEffect(() => {
        if (open) {
            if (isEditMode && customerId) {
                setIsLoading(true);
                Promise.all([fetchBranding(customerId), fetchHistory(customerId)]).then(() => {
                    if (pendingBrandingAction) {
                        setForm({
                            primaryColor: brandingConfig.primaryColor || "#001a77",
                            backgroundColor: brandingConfig.backgroundColor || tokens.colors.offWhite,
                            fontFamily: brandingConfig.fontFamily || "Roboto",
                            logoUrl: brandingConfig.logoUrl || "",
                            logoDataUrl: brandingConfig.logoDataUrl || "",
                        });
                        if (brandingConfig.logoPreviewUrl || brandingConfig.logoDataUrl) {
                            setLogoPreviewUrl(brandingConfig.logoPreviewUrl || brandingConfig.logoDataUrl || "");
                        }
                    }
                }).finally(() => {
                    setIsLoading(false);
                });
            } else {
                setForm({
                    primaryColor: brandingConfig.primaryColor || "#001a77",
                    backgroundColor: brandingConfig.backgroundColor || tokens.colors.offWhite,
                    fontFamily: brandingConfig.fontFamily || "Roboto",
                    logoUrl: brandingConfig.logoUrl || "",
                    logoDataUrl: brandingConfig.logoDataUrl || "",
                });
                if (brandingConfig.logoPreviewUrl || brandingConfig.logoDataUrl) {
                    setLogoPreviewUrl(brandingConfig.logoPreviewUrl || brandingConfig.logoDataUrl || "");
                }
            }
            setActiveStep(0);
            setMaxUnlockedStep(0);
        }
    }, [open, customerId]);

    useEffect(() => {
        loadDynamicFonts();
    }, []);

    useEffect(() => {
        let isMounted = true;
        if (logoFile) {
            fileToDataUrl(logoFile).then((dataUrl) => {
                if (isMounted) {
                    const resolved = dataUrl || "";
                    setForm((prev) => ({ ...prev, logoDataUrl: resolved }));
                    setLogoPreviewUrl(resolved);
                }
            });
            return () => {
                isMounted = false;
            };
        }
        if (form.logoUrl) {
            setLogoPreviewUrl(form.logoUrl);
            return;
        }
        if (form.logoDataUrl) {
            setLogoPreviewUrl(form.logoDataUrl);
            return;
        }
        setLogoPreviewUrl("");
    }, [logoFile, form.logoUrl]);

    const isFormChanged = useMemo(() => {
        const base = published || (isEditMode ? null : brandingConfig);
        if (!base) {
            return true;
        }

        const normalizeColor = (c?: string) => (c || "").trim().toLowerCase();
        const normalizeText = (t?: string) => (t || "").trim();

        const primaryChanged = normalizeColor(form.primaryColor) !== normalizeColor(base.primaryColor);
        const backgroundChanged = normalizeColor(form.backgroundColor) !== normalizeColor(base.backgroundColor);
        const fontChanged = normalizeText(form.fontFamily) !== normalizeText(base.fontFamily);

        const currentLogo = normalizeText(form.logoUrl || form.logoDataUrl || logoPreviewUrl);
        const baseLogo = normalizeText(base.logoUrl || base.logoDataUrl || base.logoPreviewUrl);
        const logoChanged = currentLogo !== baseLogo;

        return primaryChanged || backgroundChanged || fontChanged || logoChanged;
    }, [form, published, brandingConfig, isEditMode, logoPreviewUrl]);

    const validateStep = (stepIndex: number) => {
        const newErrors: Record<string, string> = {};
        if (stepIndex === 0) {
            // Logo is optional
        }
        if (stepIndex === 1) {
            const hexRegex = /^#([0-9A-F]{3}){1,2}$/i;
            if (!hexRegex.test(form.primaryColor)) {
                newErrors.primaryColor = "Please enter a valid hex color code (e.g. #001a77)";
            }
            if (!hexRegex.test(form.backgroundColor)) {
                newErrors.backgroundColor = "Please enter a valid hex color code (e.g. #ffffff)";
            }
            if (!form.fontFamily) {
                newErrors.fontFamily = "Please select a font family";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const goNext = () => {
        if (!validateStep(activeStep)) return;
        const nextStep = activeStep + 1;
        setActiveStep(nextStep);
        setMaxUnlockedStep((prev) => Math.max(prev, nextStep));
    };

    const goBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const onTabClick = (_e: any, newStep: number) => {
        if (newStep <= maxUnlockedStep || (newStep === 4 && (Boolean(published) || historyRows.length > 0))) {
            setActiveStep(newStep);
        }
    };

    const handleSaveOrPublish = async () => {
        if (!isFormChanged) return;

        if (!validateStep(0) || !validateStep(1)) {
            setActiveStep(1);
            return;
        }

        let resolvedLogoDataUrl = form.logoDataUrl || "";
        if (logoFile) {
            resolvedLogoDataUrl = (form.logoDataUrl || await fileToDataUrl(logoFile)) || "";
        } else if (form.logoUrl) {
            resolvedLogoDataUrl = form.logoUrl;
        }

        const configToSave: IBrandingForm = {
            primaryColor: form.primaryColor,
            backgroundColor: form.backgroundColor,
            fontFamily: form.fontFamily,
            logoUrl: form.logoUrl,
            logoDataUrl: resolvedLogoDataUrl,
            logoFile: logoFile,
            logoPreviewUrl: logoPreviewUrl,
        };

        setBrandingConfig(configToSave);
        if (onBrandingSaved) {
            onBrandingSaved(configToSave, { type: "CREATE" });
        }
        setDialogTitle("Branding Configured");
        setDialogMessage("Branding settings saved! They will be applied when the client is saved.");
        setOpenDialog(true);
        setTimeout(() => {
            onClose();
        }, 1000);
    };

    const handleApplyTheme = (row: any) => {
        const updatedConfig: IBrandingForm = {
            primaryColor: row.primaryColor || row.PrimaryColor || "#001a77",
            backgroundColor: row.backgroundColor || row.BackgroundColor || tokens.colors.offWhite,
            fontFamily: row.fontFamily || row.FontFamily || "Roboto",
            logoUrl: row.logoUrl || row.LogoUrl || "",
            logoDataUrl: row.logoDataUrl || row.LogoDataUrl || "",
            logoPreviewUrl: row.logoDataUrl || row.LogoDataUrl || row.logoUrl || row.LogoUrl || "",
        };
        setBrandingConfig(updatedConfig);
        setForm({
            primaryColor: updatedConfig.primaryColor,
            backgroundColor: updatedConfig.backgroundColor,
            fontFamily: updatedConfig.fontFamily,
            logoUrl: updatedConfig.logoUrl,
            logoDataUrl: updatedConfig.logoDataUrl,
        });
        if (updatedConfig.logoPreviewUrl) {
            setLogoPreviewUrl(updatedConfig.logoPreviewUrl);
        }
        const bId = row.brandingId ?? row.BrandingId ?? row.id ?? row.Id;
        if (onBrandingSaved) {
            onBrandingSaved(updatedConfig, {
                type: "UPDATE_STATUS",
                brandingId: bId,
                row: row,
            });
        }
        setDialogTitle("Theme Selected");
        setDialogMessage("Theme selected! It will be applied when the client is saved.");
        setOpenDialog(true);
        setTimeout(() => {
            onClose();
        }, 1000);
    };

    const renderContent = () => {
        if (activeStep === 0) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Brand Assets
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <LogoUploadField
                                fileInputKey={fileInputKey}
                                logoFile={logoFile}
                                logoPreviewUrl={logoPreviewUrl}
                                formLogoUrl={form.logoUrl}
                                onFileChange={(file) => setLogoFile(file)}
                                onClear={() => {
                                    setLogoFile(null);
                                    setForm((prev) => ({ ...prev, logoUrl: "", logoDataUrl: "" }));
                                    setLogoPreviewUrl("");
                                    setFileInputKey((prev) => prev + 1);
                                }}
                                error={errors.logo}
                                helperText={errors.logo || "Accepted: png, jpg, svg"}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TrackNowTextField
                                label="Logo URL (optional)"
                                value={form.logoUrl || ""}
                                onChange={(e: any) => setForm((prev) => ({ ...prev, logoUrl: e.target.value }))}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Box>
                        <Typography sx={{ color: tokens.colors.grey, fontSize: "14px", mb: 1 }}>Logo Preview</Typography>
                        <Paper sx={{ p: 2, minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: tokens.colors.offWhite }}>
                            {logoPreviewUrl ? (
                                <img src={logoPreviewUrl} alt="logo preview" style={{ maxHeight: 72, maxWidth: "100%" }} />
                            ) : (
                                <Typography color="text.secondary">No logo selected</Typography>
                            )}
                        </Paper>
                    </Box>

                    <Stack direction="row" justifyContent="flex-end">
                        <TrackNowButton variant="primary" onClick={goNext}>Save & Continue</TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        if (activeStep === 1) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Colors & Fonts
                    </Typography>

                    <Grid container spacing={2}>
                        <ColorField
                            label="Primary Color"
                            value={form.primaryColor}
                            onChange={(val) => setForm((prev) => ({ ...prev, primaryColor: val }))}
                            error={errors.primaryColor}
                        />
                        <ColorField
                            label="Background Color"
                            value={form.backgroundColor}
                            onChange={(val) => setForm((prev) => ({ ...prev, backgroundColor: val }))}
                            error={errors.backgroundColor}
                        />
                        <Grid item xs={12} md={6}>
                            <SearchableFontSelect
                                value={form.fontFamily}
                                onChange={(e: any) => setForm((prev) => ({ ...prev, fontFamily: e.target.value }))}
                                error={Boolean(errors.fontFamily)}
                                helperText={errors.fontFamily}
                            />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="space-between">
                        <TrackNowButton variant="tertiary" onClick={goBack}>Back</TrackNowButton>
                        <TrackNowButton variant="primary" onClick={goNext}>Save & Continue</TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        if (activeStep === 2) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Preview
                    </Typography>

                    <Paper
                        className="tn-font-preview-item"
                        style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` } as React.CSSProperties}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: `1px solid ${tokens.colors.midGrey}`,
                            backgroundColor: form.backgroundColor,
                            fontFamily: `'${form.fontFamily}', sans-serif`,
                        }}
                    >
                        <Container
                            disableGutters
                            className="tn-font-preview-item"
                            style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` } as React.CSSProperties}
                            sx={{
                                backgroundColor: tokens.colors.white,
                                borderRadius: 2,
                                px: { xs: 2, sm: 3 },
                                py: 3,
                                fontFamily: `'${form.fontFamily}', sans-serif`,
                            }}
                        >
                            <Box
                                className="tn-font-preview-item"
                                style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` } as React.CSSProperties}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: form.primaryColor,
                                    color: "#fff",
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    {logoPreviewUrl ? (
                                        <img src={logoPreviewUrl} alt="brand logo" style={{ maxHeight: 38 }} />
                                    ) : (
                                        <Box sx={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.3)" }} />
                                    )}
                                    <Typography
                                        className="tn-font-preview-item"
                                        style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` } as React.CSSProperties}
                                        sx={{ fontWeight: 700, fontFamily: `'${form.fontFamily}', sans-serif` }}
                                    >
                                        {customerName ? `${customerName} - TrackNow Portal` : "TrackNow Branding Preview"}
                                    </Typography>
                                </Stack>
                                <Typography
                                    className="tn-font-preview-item"
                                    style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` } as React.CSSProperties}
                                    sx={{ fontSize: "13px", fontFamily: `'${form.fontFamily}', sans-serif` }}
                                >
                                    {authUser?.userName || "Admin User"}
                                </Typography>
                            </Box>

                            <Stack spacing={2} sx={{ mt: 3 }}>
                                <Typography
                                    className="tn-font-preview-item"
                                    style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` } as React.CSSProperties}
                                    sx={{ color: "#333", fontSize: "15px", fontFamily: `'${form.fontFamily}', sans-serif` }}
                                >
                                    This is sample preview text demonstrating the customized font family, primary accent colors, and background theme for this client.
                                </Typography>
                                <Box sx={{ display: "inline-flex" }}>
                                    <TrackNowButton variant="primary">
                                        Primary Action Button
                                    </TrackNowButton>
                                </Box>
                            </Stack>
                        </Container>
                    </Paper>

                    <Stack direction="row" justifyContent="space-between">
                        <TrackNowButton variant="tertiary" onClick={goBack}>Back</TrackNowButton>
                        <TrackNowButton variant="primary" onClick={goNext}>Continue</TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        if (activeStep === 3) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Confirm Branding Settings
                    </Typography>

                    <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${tokens.colors.midGrey}` }}>
                        <Stack spacing={1.5}>
                            <Typography sx={{ fontWeight: 700, color: primaryColor }}>Configuration Summary</Typography>
                            <Typography><strong>Primary Color:</strong> {form.primaryColor}</Typography>
                            <Typography><strong>Background Color:</strong> {form.backgroundColor}</Typography>
                            <Typography><strong>Font Family:</strong> {form.fontFamily}</Typography>
                            <Box>
                                <Typography sx={{ mb: 1 }}><strong>Brand Logo:</strong></Typography>
                                <Paper sx={{ p: 2, minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: tokens.colors.offWhite }}>
                                    {logoPreviewUrl ? (
                                        <img src={logoPreviewUrl} alt="publish logo preview" style={{ maxHeight: 72, maxWidth: "100%" }} />
                                    ) : (
                                        <Typography color="text.secondary">No logo selected (Default will be used)</Typography>
                                    )}
                                </Paper>
                            </Box>
                        </Stack>
                    </Paper>

                    <Stack direction="row" justifyContent="space-between">
                        <TrackNowButton variant="tertiary" onClick={goBack} disabled={isPublishing}>Back</TrackNowButton>
                        <TrackNowButton variant="primary" onClick={handleSaveOrPublish} disabled={isPublishing || !isFormChanged}>
                            Save Branding
                        </TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        return (
            <Stack spacing={3}>
                <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                    Published Version & History
                </Typography>

                {published ? (
                    <>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: "rgba(0, 26, 119, 0.04)",
                                border: "1px solid rgba(0, 26, 119, 0.15)",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <InfoOutlinedIcon sx={{ color: primaryColor, fontSize: 24, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "14px", color: tokens.colors.offBlack }}>
                                <strong>Theme Information:</strong> <strong>Version 1 (V1)</strong> is the <strong>Default Theme</strong>. You can switch back to V1 or any previous version at any time from the version history below.
                            </Typography>
                        </Box>

                        <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${tokens.colors.midGrey}` }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                    <Typography><strong>Version:</strong> {published.version ? `v${published.version}` : (published.brandingId ? `#${published.brandingId}` : "-")}</Typography>
                                    <Typography><strong>Published At:</strong> {formatDate(published.createDate || published.updateDate || published.publishedAt)}</Typography>
                                    <Typography><strong>Published By:</strong> {published.userName || published.createUser || "-"}</Typography>
                                    <Typography><strong>Font:</strong> {published.fontFamily}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    {published.logoDataUrl ? <img src={published.logoDataUrl} alt="published logo" style={{ maxHeight: 72, maxWidth: "100%" }} /> : null}
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                <Box sx={{ width: 24, height: 24, borderRadius: "4px", backgroundColor: published.primaryColor }} />
                                <Box sx={{ width: 24, height: 24, borderRadius: "4px", backgroundColor: published.backgroundColor || tokens.colors.white, border: `1px solid ${tokens.colors.midGrey}` }} />
                            </Stack>
                        </Paper>

                        <Divider />

                        <Typography sx={{ fontWeight: 700, color: primaryColor }}>Version History</Typography>
                        <Table size="small" sx={{ backgroundColor: "#fff", borderRadius: 2, overflow: "hidden" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Published By</TableCell>
                                    <TableCell>Published At</TableCell>
                                    <TableCell>Font</TableCell>
                                    <TableCell>Primary</TableCell>
                                    <TableCell>Background</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyRows.length > 0 ? (
                                    historyRows.map((row, idx) => {
                                        const rowId = row.brandingId ?? row.BrandingId ?? row.id ?? row.Id;
                                        const isSelected = pendingBrandingAction?.type === "UPDATE_STATUS" && (pendingBrandingAction.brandingId === rowId);
                                        const isCurrent = Boolean(row.isDefault ?? row.IsDefault) && !pendingBrandingAction;
                                        return (
                                            <TableRow key={row.brandingId || `${row.version}-${row.createDate}-${idx}`}>
                                                <TableCell>{row.version ? `v${row.version}` : `#${row.brandingId || idx + 1}`}</TableCell>
                                                <TableCell>{row.userName || row.createUser || "-"}</TableCell>
                                                <TableCell>{formatDate(row.createDate || row.updateDate || row.publishedAt)}</TableCell>
                                                <TableCell>{row.fontFamily}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "inline-block", width: 16, height: 16, borderRadius: "3px", backgroundColor: row.primaryColor }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: "inline-block",
                                                            width: 16,
                                                            height: 16,
                                                            borderRadius: "3px",
                                                            backgroundColor: row.backgroundColor || tokens.colors.white,
                                                            border: `1px solid ${tokens.colors.midGrey}`
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {isSelected ? (
                                                        <Chip
                                                            label="Selected (Pending Save)"
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: "0.75rem",
                                                                backgroundColor: primaryColor,
                                                                color: "#fff",
                                                                fontFamily: `'${form.fontFamily}', sans-serif`,
                                                            }}
                                                        />
                                                    ) : isCurrent ? (
                                                        <Chip
                                                            label="Active Theme"
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: "0.75rem",
                                                                fontFamily: `'${form.fontFamily}', sans-serif`,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleApplyTheme(row)}
                                                            sx={{
                                                                textTransform: "none",
                                                                py: 0.25,
                                                                px: 1.5,
                                                                borderColor: primaryColor,
                                                                color: primaryColor,
                                                                fontFamily: `'${form.fontFamily}', sans-serif`,
                                                                fontWeight: 600,
                                                                borderRadius: "6px",
                                                                "&:hover": {
                                                                    borderColor: primaryColor,
                                                                    backgroundColor: `color-mix(in srgb, ${primaryColor} 8%, transparent)`,
                                                                },
                                                            }}
                                                        >
                                                            Apply
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No history records available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </>
                ) : (
                    <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${tokens.colors.midGrey}` }}>
                        <Typography color="text.secondary">
                            {isEditMode ? "No published version found yet." : "No version history for new client. Theme will be created upon saving."}
                        </Typography>
                    </Paper>
                )}
            </Stack>
        );
    };

    return (
        <ThemeProvider theme={modalTheme}>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        overflow: "hidden",
                        fontFamily: `'${form.fontFamily}', sans-serif`,
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: tokens.colors.offWhite,
                        borderBottom: `1px solid ${tokens.colors.midGrey}`,
                        px: 3,
                        py: 2,
                        fontFamily: `'${form.fontFamily}', sans-serif`,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <PaletteIcon sx={{ color: primaryColor }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: tokens.colors.offBlack, fontFamily: `'${form.fontFamily}', sans-serif` }}>
                            Branding Configuration {customerName ? `- ${customerName}` : "(Parent Client)"}
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3, backgroundColor: tokens.colors.offWhite, fontFamily: `'${form.fontFamily}', sans-serif` }}>
                    {isLoading && <Loader />}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4} lg={3}>
                            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
                                <Tabs
                                    value={activeStep}
                                    onChange={onTabClick}
                                    orientation="vertical"
                                    variant="scrollable"
                                    sx={{
                                        minHeight: 380,
                                        "& .MuiTab-root": {
                                            alignItems: "flex-start",
                                            justifyContent: "flex-start",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: "14px",
                                            color: tokens.colors.grey,
                                            px: 2,
                                            py: 1.6,
                                            fontFamily: `'${form.fontFamily}', sans-serif`,
                                        },
                                        "& .Mui-selected": {
                                            color: `${primaryColor} !important`,
                                            backgroundColor: `color-mix(in srgb, ${primaryColor} 10%, transparent)`,
                                        },
                                        "& .MuiTabs-indicator": {
                                            backgroundColor: primaryColor,
                                            width: "4px",
                                            left: 0,
                                        },
                                    }}
                                >
                                    {STEP_TABS.map((stepTitle, idx) => (
                                        <Tab
                                            key={stepTitle}
                                            label={stepTitle}
                                            disabled={!(idx === 0 || idx <= maxUnlockedStep || (idx === 4 && (Boolean(published) || historyRows.length > 0)))}
                                        />
                                    ))}
                                </Tabs>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={8} lg={9}>
                            <Paper sx={{ p: 3, borderRadius: 2, minHeight: 460 }}>
                                {renderContent()}
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>

                <CustomDialog
                    header={dialogTitle}
                    visible={openDialog}
                    onHide={() => setOpenDialog(false)}
                >
                    {dialogMessage}
                </CustomDialog>
            </Dialog>
        </ThemeProvider>
    );
};

export default CustomerBrandingModal;
